import React, { useState, useRef, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { storage } from "./Firebase";
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Form } from "react-bootstrap";
import "./ProductModal.css";
import { Editor } from "@tinymce/tinymce-react";
import useCategories from "./useCategories";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function ProductModal({
  show,
  handleClose,
  initialData,
  updateData,
  editProduct,
}) {
  const fileInputRef = useRef(null);
  // Categories created from the SubProducts page (live from Firebase)
  const { categories } = useCategories();
  // Purani (default) categories hamesha rahengi, naye categories unke saath judengi
  const defaultCategories = ["Dairy", "Breads"];
  const allCategoryNames = [
    ...defaultCategories,
    ...categories
      .map((c) => c.name)
      .filter(
        (n) => !defaultCategories.some((d) => d.toLowerCase() === n.toLowerCase())
      ),
  ];
  const [errMsgName, setErrMsgName] = useState("");
  const [errMsgDescription, setErrMsgDescription] = useState("");
  const [errMsgCategory, setErrMsgCategory] = useState("");
  const [errMsgBrand, setErrMsgBrand] = useState("");
  const [errMsgPrice, setErrMsgPrice] = useState("");
  const [errMsgMrp, setErrMsgMrp] = useState("");
  const [errMsgPkg, setErrMsgPkg] = useState("");
  const [errMsgGst, setErrMsgGst] = useState("");
  const [errMsgLaunchDate, setErrMsgLaunchDate] = useState(false);
  const [unitError, setUnitError] = useState("");

  const handleAddPackagingOption = () => {
    setSubmitProduct((prevProduct) => ({
      ...prevProduct,
      packagingOptions: [
        ...prevProduct.packagingOptions,
        { packaging: "", price: "" },
      ],
    }));
  };

  // Helper function to safely parse ANY date format (Firestore Timestamp, string, or Date)
  const formatTimestampToDate = (dateVal) => {
    if (!dateVal) return null;
    
    // 1. If it's a Firestore Timestamp with .toDate()
    if (typeof dateVal.toDate === "function") {
      return dateVal.toDate();
    }
    // 2. If it's a serialized Firestore Timestamp (often happens when passing data around)
    if (typeof dateVal === "object" && "seconds" in dateVal) {
      return new Date(dateVal.seconds * 1000);
    }
    // 3. If it's a string, try parsing it safely
    if (typeof dateVal === "string") {
      const parsedDate = new Date(dateVal);
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
    // 4. If it's already a valid Date object
    if (dateVal instanceof Date && !isNaN(dateVal.getTime())) {
      return dateVal;
    }
    
    return null;
  };

  // Initialize state WITH the formatted dates immediately so the first render doesn't crash
  const [submitProduct, setSubmitProduct] = useState(() => {
    return {
      ...initialData,
      launchDate: formatTimestampToDate(initialData.launchDate),
      preOrderReleaseDate: formatTimestampToDate(initialData.preOrderReleaseDate),
    };
  });

  // Keep the useEffect to update state if the initialData prop changes while the modal is open
  useEffect(() => {
    setSubmitProduct({
      ...initialData,
      launchDate: formatTimestampToDate(initialData.launchDate),
      preOrderReleaseDate: formatTimestampToDate(initialData.preOrderReleaseDate),
    });
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;
    const positiveWholeNumber = /^\d+$/;

    if (name === "productName") {
      if (specialCharRegex.test(value)) {
        setErrMsgName("Product name cannot contain special characters");
      } else {
        setErrMsgName("");
      }
    } else if (name === "category") {
      if (value === "") {
        setErrMsgCategory("Please select the category");
      } else {
        setErrMsgCategory("");
      }
    } else if (name === "brand") {
      if (value === "") {
        setErrMsgBrand("Please select the brand");
      }
    } else if (name === "price") {
      if (value === "") {
        setErrMsgPrice("Please enter price");
      } else if (!positiveWholeNumber.test(value)) {
        setErrMsgPrice("Price must be a positive number");
      } else if (isNaN(parseFloat(value))) {
        setErrMsgPrice("Price must be number");
      } else {
        const formattedPrice = parseFloat(value).toFixed(2);
        setErrMsgPrice("");
        setSubmitProduct((prevState) => ({
          ...prevState,
          [name]: formattedPrice,
        }));
        setErrMsgPrice("");
      }
    } else if (name === "pkgUnit") {
      setUnitError(value ? "" : "Please select the unit.");
    } else if (name === "packaging") {
      if (!positiveWholeNumber.test(value)) {
        setErrMsgPkg("Please enter a number");
      } else {
        setErrMsgPkg("");
      }
    } else if (name === "gst") {
      if (!positiveWholeNumber.test(value)) {
        setErrMsgGst("Please enter a number");
      } else {
        setErrMsgGst("");
      }
    } else if (name === "launch_date") {
      if (value === "") {
        setErrMsgLaunchDate("Please select a launch date");
      } else {
        setErrMsgLaunchDate("");
      }
    } else {
      setErrMsgName("");
      setErrMsgDescription("");
      setErrMsgCategory("");
      setErrMsgBrand("");
      setErrMsgPrice("");
      setErrMsgPkg("");
      setUnitError("");
      setErrMsgGst("");
    }

    setSubmitProduct((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEditorChange = (content) => {
    setSubmitProduct((prevState) => ({
      ...prevState,
      productDescription: content,
    }));
    setErrMsgDescription(content ? "" : "Product description is required");
  };

  const handleDate = (date) => {
    if (date) {
      setSubmitProduct((prevState) => ({
        ...prevState,
        launchDate: date,
      }));
      setErrMsgLaunchDate("");
    } else {
      setErrMsgLaunchDate("Please select a valid date.");
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const newImageName = `${e.target.files[0].name}_${Date.now()}`;
      const storageRef = ref(storage, `products_images/${newImageName}`);

      uploadBytes(storageRef, e.target.files[0]).then((data) => {
        getDownloadURL(data.ref).then((val) => {
          setSubmitProduct((prevState) => ({
            ...prevState,
            image: val,
            image_transparent_bg: val,
          }));
        });
      });
    }
  };

  const handleLogistic = () => {
    setSubmitProduct((prevState) => ({
      ...prevState,
      enableLogistic: !submitProduct.enableLogistic,
    }));
  };

  const handlePublishonlyonCRM = () => {
    setSubmitProduct((prevState) => ({
      ...prevState,
      publishOnCRM: !submitProduct.publishOnCRM,
      inStock: false,
    }));
  };

  // 👇 PRE-ORDER HANDLERS 👇
  const handlePreOrder = () => {
    setSubmitProduct((prevState) => ({
      ...prevState,
      isPreOrder: !prevState.isPreOrder,
    }));
  };

  const handlePreOrderDate = (date) => {
    if (date) {
      setSubmitProduct((prevState) => ({
        ...prevState,
        preOrderReleaseDate: date,
      }));
    }
  };

  const isEmpty = (val) => val === undefined || val === null || String(val).trim() === "";

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields before saving (each error is shown as a toast)
    let isValid = true;
    const showError = (msg) => {
      toast.error(msg, { toastId: msg }); // toastId stops the same toast from stacking
      isValid = false;
    };

    const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;
    if (isEmpty(submitProduct.productName)) {
      showError("Product name is required");
    } else if (specialCharRegex.test(submitProduct.productName)) {
      showError("Product name cannot contain special characters");
    }

    if (isEmpty(submitProduct.category)) {
      showError("Please select the category");
    }

    const options = submitProduct.packagingOptions || [];
    if (options.some((opt) => isEmpty(opt.price))) {
      showError("Please enter price");
    }
    if (options.some((opt) => isEmpty(opt.priceBeforeDiscount))) {
      showError("Please enter MRP");
    }

    if (!isValid) return; // Block save until all required fields are filled

    updateData(submitProduct);
    handleClose();
  };

  const handlePackagingChange = (index, e) => {
    const { name, value } = e.target;
    console.log("handlePackagingChange ", name, value);

    if (name === "price" && !isEmpty(value)) setErrMsgPrice("");
    if (name === "priceBeforeDiscount" && !isEmpty(value)) setErrMsgMrp("");
    
    const updatedPackagingOptions = [...submitProduct.packagingOptions];
    updatedPackagingOptions[index][name] = value;
    setSubmitProduct((prevProduct) => ({
      ...prevProduct,
      packagingOptions: updatedPackagingOptions,
    }));
  };

  const handleDeleteInput = (index) => {
    const updatedPackagingOptions = [...submitProduct.packagingOptions];
    updatedPackagingOptions.splice(index, 1);

    setSubmitProduct((prevProduct) => ({
      ...prevProduct,
      packagingOptions: updatedPackagingOptions,
    }));
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form className="product-modal" onSubmit={handleSubmit}>
            <div className="">
              <span className="modal-heading">General Information : </span>
              <input
                className="name-input"
                type="text"
                placeholder="Product Name"
                name="productName"
                value={submitProduct.productName}
                onChange={handleChange}
                required
                autoComplete="off"
              />
            </div>
            <div className="first-container ">
              <div className="editor-container ">
                <Editor
                  apiKey="o7qiqe8bed298gt7bdpx1n1o2zi63hgfpom6rnyd6wmx5g79"
                  value={submitProduct.productDescription}
                  init={{
                    plugins:
                      "anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount",
                    toolbar:
                      "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | numlist bullist indent outdent | emoticons charmap | removeformat",
                  }}
                  onEditorChange={handleEditorChange}
                />
                {errMsgDescription && (
                  <span
                    style={{
                      color: "red",
                      fontSize: "12px",
                      display: "block",
                      marginTop: "5px",
                    }}
                  >
                    {errMsgDescription}
                  </span>
                )}
              </div>
              <div className="detail-container">
                <div className="detail-first-sub-container">
                  <div className="category-and-brand" style={{ flexWrap: "wrap" }}>
                    <strong>Category</strong>
                    <select
                      className=""
                      name="category"
                      value={submitProduct.category || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="" disabled>
                        Select Category
                      </option>
                      {/* Default categories + categories created from SubProducts page */}
                      {allCategoryNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                      {/* Keep an old/removed category visible while editing an existing product */}
                      {submitProduct.category &&
                        !allCategoryNames.includes(submitProduct.category) && (
                          <option value={submitProduct.category}>
                            {submitProduct.category}
                          </option>
                        )}
                    </select>
                  </div>
                  <div className="category-and-brand" style={{ flexWrap: "wrap" }}>
                    <strong>Brand</strong>
                    <select
                      className=""
                      name="brand"
                      value={submitProduct.brand}
                      onChange={handleChange}
                    >
                      <option disabled selected>
                        Select Brand
                      </option>
                      <option value="Whyte Farms" selected>
                        Whyte Farms
                      </option>
                    </select>
                    {errMsgBrand && (
                      <span
                        style={{
                          color: "red",
                          fontSize: "12px",
                          display: "block",
                          width: "100%",
                          flexBasis: "100%",
                          marginTop: "4px",
                          marginBottom: "10px",
                        }}
                      >
                        {errMsgBrand}
                      </span>
                    )}
                  </div>
                </div>
                <div className="detail-second-sub-container">
                  <div className="">
                    {submitProduct.image ? (
                      <div className="image-comtainer" style={{}}>
                        <img
                          src={submitProduct.image}
                          alt="Preview"
                          style={{
                            height: "250px",
                            marginTop: "10px",
                            display: "block",
                          }}
                        />
                        <input
                          type="file"
                          accept="image/*"
                          id="image"
                          className="form-control"
                          style={{
                            height: "34px",
                            padding: "6px 10px",
                            boxSizing: "border-box",
                            border: "2px solid #ccc",
                            borderRadius: "4px",
                            backgroundColor: "#f8f8f8",
                            fontSize: "16px",
                            resize: "none",
                          }}
                          onChange={handleImageChange}
                          ref={fileInputRef}
                        />
                      </div>
                    ) : (
                      <input
                        type="file"
                        accept="image/*"
                        id="image"
                        className="form-control"
                        style={{
                          height: "34px",
                          padding: "6px 10px",
                          boxSizing: "border-box",
                          border: "2px solid #ccc",
                          borderRadius: "4px",
                          backgroundColor: "#f8f8f8",
                          fontSize: "16px",
                          resize: "none",
                        }}
                        onChange={handleImageChange}
                        ref={fileInputRef}
                      />
                    )}
                  </div>
                  <div className="tax-container">
                    <div className="first">
                      <h5 className="">Tax Information</h5>
                      <span className="tax-info">
                        <input
                          className=""
                          type="number"
                          name="gst"
                          value={submitProduct.gst}
                          placeholder="GST"
                          onChange={handleChange}
                          required
                        />
                        <span className="percentage pr-2">%</span>
                      </span>

                      {errMsgGst && (
                        <span
                          style={{
                            color: "red",
                            fontSize: "12px",
                            display: "block",
                            marginBottom: "10px",
                          }}
                        >
                          {errMsgGst}
                        </span>
                      )}
                    </div>
                   <div className="date-container">
  <h5 className="">Launch Date</h5>
  <DatePicker
    className="date h-10 w-3/4 bg-white rounded-lg border px-2"
    name="preOrderReleaseDate" // ❌ Wrong name
    selected={submitProduct.preOrderReleaseDate || null} // ❌ Wrong state
    onChange={handlePreOrderDate} // ❌ Wrong handler
    dateFormat="dd/MM/yyyy"
    placeholderText="Select release date"
  />

                      {errMsgLaunchDate && (
                        <span
                          style={{
                            color: "red",
                            fontSize: "12px",
                            display: "block",
                            marginBottom: "10px",
                          }}
                        >
                          {errMsgLaunchDate}
                        </span>
                      )}
                    </div>
                    <div
                      className=""
                      style={{
                        backgroundColor: "#f3f4f6",
                        borderRadius: "0.75rem",
                        padding: "0.5rem",
                      }}
                    >
                      <Form.Switch
                        id="toggle-reverseLogistic"
                        label="Enable Reverse Logistic"
                        checked={submitProduct.enableLogistic}
                        onChange={handleLogistic}
                      />
                      <Form.Switch
                        id="toggle-enableOnApp"
                        label="Enable CRM only"
                        checked={submitProduct.publishOnCRM}
                        onChange={handlePublishonlyonCRM}
                      />

                      {/* 👇 PRE-ORDER UI 👇 */}
                      <Form.Switch
                        id="toggle-preOrder"
                        label="Enable Pre-Order"
                        checked={submitProduct.isPreOrder || false}
                        onChange={handlePreOrder}
                      />
                      
                      {submitProduct.isPreOrder && (
                        <div style={{ marginTop: "10px", padding: "10px", backgroundColor: "#fff", borderRadius: "8px" }}>
                          <h5 style={{ fontSize: "14px", marginBottom: "5px" }}>Expected Release Date</h5>
                          <DatePicker
                            className="date h-10 w-3/4 bg-white rounded-lg border px-2"
                            name="preOrderReleaseDate"
                            selected={submitProduct.preOrderReleaseDate || null}
                            onChange={handlePreOrderDate}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select release date"
                          />
                        </div>
                      )}
                      {/* 👆 PRE-ORDER UI 👆 */}

                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="second-container">
              <div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                  }}
                >
                  {submitProduct.packagingOptions.map((option, index) => {
                    return (
                      <div
                        key={index}
                        style={{
                          width: "300px",
                          backgroundColor: "#f3f4f6",
                          borderRadius: "0.75rem",
                        }}
                      >
                        <div className="packing">
                          <h5>Packaging</h5>
                          <div>
                            <input
                              type="text"
                              placeholder="Packaging"
                              name="packaging"
                              value={option.packaging}
                              onChange={(e) => handlePackagingChange(index, e)}
                              required
                            />
                            <select
                              name="pkgUnit"
                              value={option.pkgUnit}
                              onChange={(e) => handlePackagingChange(index, e)}
                              required
                            >
                              <option disabled selected>
                                Select Unit
                              </option>
                              <option value="ltr">Liter (ltr)</option>
                              <option value="ml">Milliliter (ml)</option>
                              <option value="kg">Kilogram (kg)</option>
                              <option value="g">Gram (g)</option>
                              <option value="pieces">Pieces</option>
                            </select>
                          </div>
                          {errMsgPkg && (
                            <span
                              style={{
                                color: "red",
                                fontSize: "12px",
                                display: "block",
                                marginBottom: "10px",
                              }}
                            >
                              {errMsgPkg}
                            </span>
                          )}

                          {unitError && (
                            <span
                              style={{
                                color: "red",
                                fontSize: "12px",
                                display: "block",
                                marginBottom: "10px",
                              }}
                            >
                              {unitError}
                            </span>
                          )}
                        </div>

                        <div className="price">
                          <h5>Price</h5>
                          <input
                            type="number"
                            placeholder="price"
                            name="price"
                            value={option.price}
                            onChange={(e) => handlePackagingChange(index, e)}
                            required
                          />
                        </div>
                        <div className="price">
                          <h5>MRP</h5>
                          <input
                            type="number"
                            placeholder="MRP"
                            name="priceBeforeDiscount"
                            value={option.priceBeforeDiscount}
                            onChange={(e) => handlePackagingChange(index, e)}
                            required
                          />
                          {submitProduct.packagingOptions.length > 1 && (
                            <button onClick={() => handleDeleteInput(index)}>
                              Remove Packaging
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Button
                  variant="primary"
                  onClick={handleAddPackagingOption}
                  style={{
                    marginTop: "8px",
                    justifyContent: "end",
                  }}
                >
                  Add More Packaging
                </Button>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ProductModal;